import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Clock, Facebook, Linkedin, Mail, Twitter } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import DiscoveryCallDialog from "@/components/DiscoveryCallDialog";
import SEOHead from "@/components/SEOHead";
import PostDate from "@/components/PostDate";
import { buildBreadcrumb } from "@/lib/seo/schemas";
import { toIsoDate } from "@/lib/postDate";
import { useBlogPost, usePublishedBlogPosts } from "@/lib/cms";
import type { BlogContentBlock } from "@/data/blogPosts";
import mahirahImgAsset from "@/assets/mahirah.jpeg.asset.json";
const mahirahImg = mahirahImgAsset.url;

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const renderBlock = (block: BlogContentBlock, index: number) => {
  switch (block.type) {
    case "heading":
      return <h2 key={index} className="text-2xl font-heading font-bold text-foreground md:text-3xl">{block.content}</h2>;
    case "paragraph":
      return <p key={index} className="text-lg leading-8 text-muted-foreground md:text-xl">{block.content}</p>;
    case "list": {
      const ListTag = block.ordered ? "ol" : "ul";
      return (
        <ListTag key={index} className={`space-y-3 pl-6 text-lg leading-8 text-muted-foreground md:text-xl ${block.ordered ? "list-decimal" : "list-disc"}`}>
          {(block.items ?? []).map((item) => <li key={item}>{item}</li>)}
        </ListTag>
      );
    }
    case "highlights":
      return (
        <section key={index} className="space-y-5">
          <h2 className="text-2xl font-heading font-bold text-foreground md:text-3xl">{block.title}</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {block.items.map((item) => (
              <div key={item.title} className="rounded-2xl border bg-card p-5 shadow-sm">
                <h3 className="text-lg font-heading font-bold text-foreground">{item.title}</h3>
                <p className="mt-2 text-base leading-7 text-muted-foreground md:text-lg">{item.description}</p>
              </div>
            ))}
          </div>
        </section>
      );
    case "faq":
      return (
        <section key={index} className="space-y-5">
          <h2 className="text-2xl font-heading font-bold text-foreground md:text-3xl">{block.title}</h2>
          <div className="space-y-4">
            {block.items.map((item) => (
              <div key={item.question} className="rounded-2xl border bg-card p-5 shadow-sm">
                <h3 className="text-lg font-heading font-bold text-foreground">{item.question}</h3>
                <p className="mt-2 text-base leading-7 text-muted-foreground md:text-lg">{item.answer}</p>
              </div>
            ))}
          </div>
        </section>
      );
    case "quote":
      return (
        <blockquote key={index} className="rounded-2xl border-l-4 border-accent bg-muted/40 px-6 py-5">
          <p className="text-lg font-medium leading-8 text-foreground md:text-xl">“{block.content}”</p>
          {block.attribution ? <footer className="mt-3 text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground">{block.attribution}</footer> : null}
        </blockquote>
      );
    default:
      return null;
  }
};

const BlogPost = () => {
  const { slug = "" } = useParams();
  const { data: post, isLoading } = useBlogPost(slug);
  const { data: posts = [] } = usePublishedBlogPosts();
  const related = posts.filter((p) => p.slug !== slug).slice(0, 3);

  const readableTitle = slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

  if (isLoading) {
    return (
      <>
        <SEOHead
          title={`${readableTitle} | MVIBE`}
          description={`Expert insights from MVIBE on ${readableTitle.toLowerCase()} and practical workplace growth.`}
          canonical={`https://mvibeon.com/blog/${slug}`}
        />
        <section className="bg-background py-16">
          <div className="container mx-auto max-w-3xl px-4">
            <article className="space-y-8">
              <header className="space-y-4 text-center">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">MVIBE Insights</p>
                <h1 className="text-3xl font-heading font-bold text-foreground md:text-5xl">{readableTitle}</h1>
                <h2 className="text-xl font-heading font-semibold text-foreground md:text-2xl">Preparing the full article</h2>
                <p className="text-lg leading-8 text-muted-foreground md:text-xl">
                  This page is loading a practical MVIBE article focused on workplace growth, communication, leadership,
                  and behavioural change that can be applied in real conversations and team settings.
                </p>
              </header>

              <section className="space-y-4 rounded-2xl border bg-card p-6 shadow-sm">
                <h3 className="text-lg font-heading font-bold text-foreground md:text-xl">What this page covers</h3>
                <p className="text-base leading-8 text-muted-foreground md:text-lg">
                  MVIBE articles are written for professionals, managers, founders, HR teams, and learners who want clear,
                  practical guidance instead of vague motivational advice. Each article is structured to turn a workplace
                  challenge into actionable next steps that are easy to understand and apply.
                </p>
                <p className="text-base leading-8 text-muted-foreground md:text-lg">
                  Depending on the topic, the article may explore communication breakdowns, behavioural patterns,
                  confidence gaps, leadership blind spots, collaboration challenges, or ways to improve team culture.
                  The goal is always the same: help readers move from awareness to action with more clarity and confidence.
                </p>
                <p className="text-base leading-8 text-muted-foreground md:text-lg">
                  Once fully loaded, this page will include structured sections, useful examples, and practical takeaways
                  designed to support stronger conversations, healthier workplace behaviour, and more intentional growth
                  for individuals as well as teams.
                </p>
              </section>
            </article>
          </div>
        </section>
      </>
    );
  }

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = encodeURIComponent(post.title);
  const shareUrlEncoded = encodeURIComponent(shareUrl);

  return (
    <>
      <SEOHead
        title={post.seo?.title ?? `${post.title} | MVIBE Blog`}
        description={post.seo?.description ?? post.excerpt}
        canonical={post.seo?.canonical ?? `https://mvibeon.com/blog/${post.slug}`}
        ogTitle={post.seo?.ogTitle ?? post.title}
        ogDescription={post.seo?.ogDescription ?? post.excerpt}
        ogImage={post.seo?.ogImage ?? post.image}
        type={post.seo?.type ?? "article"}
        schema={post.seo?.schema ?? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: post.image,
            datePublished: toIsoDate(post.publishedAt) ?? post.date,
            dateModified: toIsoDate(post.publishedAt) ?? post.date,
            author: {
              "@type": "Person",
              name: "Mahirah",
              url: "https://mvibeon.com/about",
            },
            publisher: {
              "@type": "Organization",
              name: "MVIBE",
              logo: { "@type": "ImageObject", url: "https://mvibeon.com/logo.png" },
            },
            mainEntityOfPage: `https://mvibeon.com/blog/${post.slug}`,
          },
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
        ]}
      />

      <section className="bg-primary py-12 text-primary-foreground">
        <div className="container mx-auto max-w-3xl px-4 text-center">
          <nav className="mb-4 flex items-center justify-center gap-1.5 text-base text-primary-foreground/60">
            <Link to="/" className="transition-colors hover:text-primary-foreground">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/blog" className="transition-colors hover:text-primary-foreground">Blog</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="max-w-[200px] truncate text-primary-foreground">{post.title}</span>
          </nav>
          <motion.div variants={fadeUp} initial="hidden" animate="visible" transition={{ duration: 0.6 }}>
            <Badge className="mb-3 bg-accent text-accent-foreground">{post.category}</Badge>
            <h1 className="text-2xl font-heading font-bold leading-tight md:text-4xl">{post.title}</h1>
            <div className="mt-4 flex items-center gap-3">
              <img src={mahirahImg} alt="Mahirah" className="h-10 w-10 rounded-full object-cover" />
              <div>
                <p className="text-base font-semibold">Mahirah</p>
                <p className="text-sm text-primary-foreground/70">Executive Facilitator | Soft Skills Trainer | Life Coach | Founder – MVIBE</p>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-center gap-4 text-base text-primary-foreground">
              <PostDate publishedAt={post.publishedAt} label={post.date} />
              <span>•</span>
              <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{post.readTime} read</span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-background py-12">
        <div className="container mx-auto max-w-3xl px-4">
          <motion.article variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }} className="space-y-8">
            <img src={post.image} alt={post.title} className="mb-6 w-full rounded-xl" loading="lazy" />
            <div className="rounded-2xl border-l-4 border-primary bg-primary/5 px-6 py-5">
              <p className="text-lg leading-8 text-foreground md:text-xl">{post.excerpt}</p>
            </div>
            {post.content.map((block, index) => renderBlock(block, index))}
          </motion.article>

          <div className="mt-10 flex items-center gap-4 border-t pt-6">
            <span className="text-base font-semibold text-foreground">Share this article:</span>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrlEncoded}`} target="_blank" rel="noopener noreferrer" className="text-muted-foreground transition-colors hover:text-accent"><Linkedin className="h-5 w-5" /></a>
            <a href={`https://twitter.com/intent/tweet?text=${shareTitle}&url=${shareUrlEncoded}`} target="_blank" rel="noopener noreferrer" className="text-muted-foreground transition-colors hover:text-accent"><Twitter className="h-5 w-5" /></a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrlEncoded}`} target="_blank" rel="noopener noreferrer" className="text-muted-foreground transition-colors hover:text-accent"><Facebook className="h-5 w-5" /></a>
            <a href={`mailto:?subject=${shareTitle}&body=${shareUrlEncoded}`} className="text-muted-foreground transition-colors hover:text-accent"><Mail className="h-5 w-5" /></a>
          </div>
        </div>
      </section>

      <section className="bg-muted/30 py-12">
        <div className="container mx-auto px-4">
          <h2 className="mb-8 text-center text-2xl font-heading font-bold text-foreground md:text-3xl">Related Articles</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {related.map((r, i) => (
              <motion.article key={r.slug} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} whileHover={{ y: -4 }} className="overflow-hidden rounded-xl border bg-card">
                <img src={r.image} alt={r.title} loading="lazy" className="h-40 w-full object-cover" />
                <div className="p-4">
                  <Badge className="mb-1.5 bg-accent text-accent-foreground">{r.category}</Badge>
                  <h3 className="mb-1.5 text-base font-heading font-bold leading-snug text-foreground">{r.title}</h3>
                  <Link to={`/blog/${r.slug}`} className="text-base font-semibold text-primary hover:underline">Continue Reading →</Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-accent py-10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-3 text-2xl font-heading font-bold text-accent-foreground md:text-3xl">Found this helpful? Book a Discovery Call</h2>
          <p className="mb-5 text-base text-accent-foreground/80">Let’s explore how MVIBE can support your growth journey.</p>
          <DiscoveryCallDialog><Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">Book a Discovery Call</Button></DiscoveryCallDialog>
        </div>
      </section>
    </>
  );
};

export default BlogPost;
